import { createContext, ReactElement } from "react";
import { PHONE_NUMBER_PREFIX } from "@zenith/data/constants";
import { OperationDTO } from "@zenith/services/transactions";
import {
  OutletListDTO,
  PosOfferReviewResponseDtoNG,
  PosSubmitOfferRequestDtoNG,
} from "@zenith/services/user";
import { UserRole } from "@zenith/types";
import { useGlobalContext } from "./hooks";
import {
  ChildrenType,
  GlobalContextType,
  GlobalContextDispatchType,
  UseGlobalContextType,
  PaymentsContextType,
  SendMoneyContextType,
  TransactionsContextType,
  SelfiesContextType,
  PosContextType,
  RegistrationContextType,
  SignUpContextType,
  UserDetailsContextType,
  PaymentLinkContextType,
  LoansContextType,
  SettingsContextType,
  PersonalDetailsContextType,
  UpgradeContextType,
  OutletContextType,
  OutletSelection,
} from "./types";

export const initGlobalContext: GlobalContextType = {
  loadingContext: {
    loading: false,
  },

  zenithAccountUpgradeContext: {
    isUpgrade: false,
    accountNumber: "",
  },

  mobileVerificationContext: {
    countryCode: "",
    email: "",
    phoneNumber: "",
    firstName: "",
    isNewUser: false,
  },
  notificationContext: {
    open: false,
  },
  appStepperContext: {
    activeStep: 0,
    maxStep: 0,
  },
  paymentsContext: {
    paymentMethod: undefined,
    paymentStatus: OperationDTO.status.PENDING,
    expirationDate: "",
    totalAmount: 0,
    transactionId: "",
    operationId: "",
    transactionInfo: {
      transactionDate: "",
    },
  },
  serviceFailureContext: {
    hasError: false,
  },
  sendMoneyContext: {
    accountOwner: "",
    accountNumber: "",
    amount: 0,
    bankId: "",
    bankName: "",
    beneficiaryAccountNumber: "",
    beneficiaryName: "",
    categoryId: "",
    categoryName: "",
    description: "",
    feeValue: 0,
    sessionId: "",
    status: OperationDTO.status.PENDING,
    totalAmount: 0,
    transactionDate: "",
    operationId: "",
    errorMessage: "",
    vatValue: 0,
  },
  transactionsContext: {
    accountOwner: "",
    amount: 0,
    bankId: "",
    bankName: "",
    beneficiaryAccountNumber: "",
    beneficiaryName: "",
    refundReason: "",
    feeValue: 0,
    sessionId: "",
    status: OperationDTO.status.PENDING,
    totalAmount: 0,
    transactionDate: "",
    refundedOperationId: "",
    operationId: "",
    errorMessage: "",
    hasTransactions: false,
    totalNoOfTransactions: 0,
    isOperation: false,
    terminalId: "",
    description: "",
    disputeId: "",
  },
  selfiesContext: {
    imageContent: "",
    imageType: "",
  },
  posContext: {
    date: "",
    delivery: {},
    fees: {},
    paymentMethod: PosOfferReviewResponseDtoNG.paymentMethod.BANK_TRANSFER,
    paymentOption: PosOfferReviewResponseDtoNG.paymentOption.TRANSACTION_FEE,
    phoneNumber: "",
    requestId: "",
    deviceId: "",
    selectedOffers: [],
    orderedDevices: {},
    submitDTO: {
      accountNumber: "",
      delivery: {
        address: {
          area: "",
          state: "",
          streetName: "",
        },
        recipientName: "",
      },
      selectedOffers: [],
      paymentMethod: PosSubmitOfferRequestDtoNG.paymentMethod.BANK_TRANSFER,
      paymentOption: PosSubmitOfferRequestDtoNG.paymentOption.TRANSACTION_FEE,
    },
    feesSubmit: {},
    submissionInfo: {},
    showAccountLockedModal: false,
    isPosOrderSubmitted: true,
    isReturnPosButtonDisplayed: false,
    xmfa: "",
  },
  registrationContext: {
    isRegistrationCompleted: true,
    isBusinessSectorCompleted: true,
  },
  signUpContext: {
    countryCode: "",
    phoneNumber: "",
    encryptedPin: "",
    userExternalId: "",
  },
  userDetailsContext: {
    id: "",
    role: UserRole.EMPLOYEE,
    bankVerificationNumber: "",
    businessName: "",
    countryCode: "",
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    physicalBusinessAddress: "",
    tradingName: "",
    walletId: "",
    birthdate: "",
    state: "LOADING",
    availableBalance: null,
    pendingBalance: null,
    ledgerBalance: null,
    ledgerBalanceTooltip: "",
    isBalanceVisible: true,
    userSettingsPreference: null,
    isLoadingUser: true,
    walletError: false,
  },
  paymentLinkContext: {
    totalAmount: 0,
    transactionId: "",
    paymentMethod: "",
    createdAt: "",
    salesItems: [],
    paymentLink: {
      businessTradingName: "",
      expirationDate: "",
    },
    clientData: {
      countryCode: PHONE_NUMBER_PREFIX,
      email: "",
      fullName: "",
      phoneNumber: "",
    },
    isValid: false,
  },
  loansContext: {
    packageName: "",
    loanRequested: 0,
    loanRepay: 0,
    interestRate: 0,
    duration: "",
  },
  settingsContext: {
    newUserAdded: false,
    showModal: false,
    updateList: false,
  },
  personalDetailsContext: {
    documentPhoneNumber: "",
    email: "",
    bankVerificationNumber: "",
    mismatchFoundInDocument: false,
  },
  upgradeContext: {},
  outletContext: {
    outlets: [],
    isLoadingOutlets: true,
    isLoadingBalances: true,
    selection: null,
  },
};

const initGlobalContextDispatch: GlobalContextDispatchType = {
  clearAllContexts: () => undefined,
  clearUnusedContexts: () => undefined,
  setLoading: (loading: boolean) => undefined,
  setIsUpgrade: (isUpgrade: boolean) => undefined,
  setAccountNumber: (accountNumber: string) => undefined,
  setCountryCode: (countryCode: string) => undefined,
  setEmail: (email: string) => undefined,
  setPhoneNumber: (phoneNumber: string) => undefined,
  setFirstName: (firstName: string) => undefined,
  setIsNewUser: (isNewUser: boolean) => undefined,
  setOpen: (open: boolean) => undefined,
  clearNotificationContext: () => undefined,
  setActiveStep: (ActiveStep: number) => undefined,
  setMaxStep: (maxStep: number) => undefined,
  setPaymentsContext: (status: PaymentsContextType) => undefined,
  clearPaymentsContext: () => undefined,
  setServiceFailureContext: (hasError: boolean) => undefined,
  setSendMoneyContext: (state: SendMoneyContextType) => undefined,
  clearSendMoneyContext: () => undefined,
  setTransactionsContext: (state: TransactionsContextType) => undefined,
  clearTransactionsContext: () => undefined,
  setSelfiesContext: (state: SelfiesContextType[]) => undefined,
  setPosContext: (state: PosContextType) => undefined,
  clearPosContext: () => undefined,
  setRegistrationContext: (state: RegistrationContextType) => undefined,
  setSignUpContext: (state: Partial<SignUpContextType>) => undefined,
  setUserDetailsContext: (state: Partial<UserDetailsContextType>) => undefined,
  setPaymentLinkContext: (state: PaymentLinkContextType) => undefined,
  clearPaymentLinkContext: () => undefined,
  setLoansContext: (state: LoansContextType) => undefined,
  clearLoansContext: () => undefined,
  setSettingsContext: (state: SettingsContextType) => undefined,
  clearSettingsContext: () => undefined,
  setPersonalDetailsContext: (state: PersonalDetailsContextType) => undefined,
  clearPersonalDetailsContext: () => undefined,
  setUpgradeContext: (state: UpgradeContextType) => undefined,
  clearUpgradeContext: () => undefined,
  setOutletContext: (state: Partial<OutletContextType>) => undefined,
  setOutlets: (outlets: OutletListDTO[]) => undefined,
  setOutletSelection: (selection: OutletSelection) => undefined,
  clearOutletContext: () => undefined,
};

const initContextState: UseGlobalContextType = {
  globalContext: initGlobalContext,
  globalContextDispatch: initGlobalContextDispatch,
};

/**
 * Global Context of the application.
 */
export const GlobalContext =
  createContext<UseGlobalContextType>(initContextState);

/**
 * The Global Context provider.
 * @param {ChildrenType} param Any children component.
 * @returns {ReactElement} A react element (GlobalContextProvider).
 */
export const GlobalContextProvider = ({
  children,
}: ChildrenType): ReactElement => {
  return (
    <GlobalContext.Provider value={useGlobalContext(initGlobalContext)}>
      {children}
    </GlobalContext.Provider>
  );
};
